import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  MenuItem,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiHelpCircle, FiX } from 'react-icons/fi';
import { FiFile } from 'react-icons/all';
import { AxiosRequestConfig } from 'axios';
import { STORAGE } from '~/constants';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { SelectItem } from '~/types';
import { Heading, Input, ReactSelect, Select, Textarea } from '~/components';
import {
  useSeriesNumber,
  useSinister,
  useSinistersTypes,
  useSinistersTypesAsSelect,
} from '~/hooks/useSinisters/useSinisters';
import { SeriesNumber } from '~/types/sinisters';
import { queryClient } from '~/utils/react-query';
import apiSinister from '~/utils/apiSinister';

export const SinistersForm: React.FC<{
  isEdit: boolean;
  sinisterId?: number;
  link?: boolean;
}> = ({ isEdit, sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const [nmr_serie, setNmrSerie] = useState<SeriesNumber[]>([]);
  const [selectedNmrSerie, setSelectedNmrSerie] = useState<SeriesNumber[]>([]);
  const [descricao, setDescricao] = useState<string>('');
  const [tipo, setTipo] = useState<number | undefined>(undefined);

  const { data: types, isLoading: isTypeLoading } = useSinistersTypesAsSelect();
  const { data: typesAux, isLoading: isTypeAuxLoading } = useSinistersTypes();

  const declaracaoRef = useRef<HTMLInputElement | null>(null);
  const boletimRef = useRef<HTMLInputElement | null>(null);
  const outroRef = useRef<HTMLInputElement | null>(null);
  const apoliceRef = useRef<HTMLInputElement | null>(null);

  const [declaracao, setSelectedDeclaracao] = useState<any>();
  const [nomeDeclaracao, setNomeDeclaracao] = useState<string | undefined>('');
  const [boletim, setSelectedBoletim] = useState<any>();
  const [nomeBoletim, setNomeBoletim] = useState<string | undefined>('');
  const [outro, setSelectedOutro] = useState<any>();
  const [nomeOutro, setNomeOutro] = useState<string | undefined>('');
  const [apolice, setSelectedApolice] = useState<any>();
  const [nomeApolice, setNomeApolice] = useState<string | undefined>('');

  const [possui_apolice, setPossuiApolice] = useState<boolean>(false);
  const [revisaoComentario, setRevisaoComentario] = useState<
    string | undefined
  >('');
  const [mostrarBoletimAlert, setMostrarBoletimAlert] =
    useState<boolean>(false);

  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '');
  const { groups, headOffices, branches } = useContext(FilterContext);
  const [buscaNmrSerie, setBuscaNmrSerie] = useState<string>('');

  const {
    data: series,
    isLoading: isNmrSerieLoading,
    refetch,
  } = useSeriesNumber(
    buscaNmrSerie,
    {
      onSuccess: r => {
        setNmrSerie(r.results);
      },
    },
    { groups, headOffices, branches },
  );

  useEffect(() => {
    if (link) handleOpen();
    refetch();
  }, [groups, headOffices, branches, buscaNmrSerie]);

  const {
    isOpen: isVisible,
    onClose: onCloseAlert,
    onOpen: onOpenAlert,
  } = useDisclosure({ defaultIsOpen: true });

  const handleOpen = () => {
    if (isEdit && sinisterId) {
      apiSinister.get(`sinistros/${sinisterId}`).then(res => {
        const sinister = res.data;
        setSelectedNmrSerie([
          {
            numero_serie: sinister.numero_serie,
            id_empresa: sinister.empresa.id_empresa,
            id_contrato: sinister.contrato.id_contrato,
          },
        ]);
        setDescricao(sinister.descricao);
        setTipo(Number(sinister.tipo.id));
        setNomeDeclaracao(sinister.declaracao);
        setNomeBoletim(sinister.boletim_ocorrencia);
        setNomeOutro(sinister.outro);
        setPossuiApolice(sinister.apolice_propria);
        if (sinister.apolice_propria) {
          setNomeApolice(sinister.apolice_seguro);
        }
        setRevisaoComentario(sinister.revisao_comentario);
        setMostrarBoletimAlert(sinister.tipo.requer_boletim_ocorrencia);
      });
    }
    refetch();
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setSelectedNmrSerie([]);
    setDescricao('');
    setTipo(undefined);
    setSelectedDeclaracao(null);
    setNomeDeclaracao('');
    setSelectedBoletim(null);
    setNomeBoletim('');
    setSelectedOutro(null);
    setNomeOutro('');
    setSelectedApolice(null);
    setNomeApolice('');
    setPossuiApolice(false);
    setRevisaoComentario('');
    setMostrarBoletimAlert(false);
    onCloseAlert();
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    const ns = selectedNmrSerie.map(item => {
      return {
        numero_serie: item.numero_serie,
        id_contrato: item.id_contrato,
        id_empresa: item.id_empresa,
      };
    });

    !(declaracao || nomeDeclaracao) &&
      setError('declaracao', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    !(boletim || nomeBoletim) &&
      mostrarBoletimAlert &&
      setError('boletim', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    possui_apolice &&
      !(apolice || nomeApolice) &&
      setError('apolice', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });

    (!descricao || !descricao.trim().length) &&
      setError('descricao', {
        type: 'required',
        message: 'Descrição é obrigatória',
      });

    !tipo &&
      setError('tipo', {
        type: 'required',
        message: 'Tipo de sinistro é obrigatório',
      });

    !selectedNmrSerie.length &&
      setError('nmr_serie', {
        type: 'required',
        message: 'Número de série é obrigatório',
      });

    descricao.length > 255 &&
      setError('descricao', {
        type: 'maxLength',
        message: 'Observações com mais de 255 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = new FormData();
    if (tipo) {
      form.append('tipo', tipo.toString());
    }
    form.append('descricao', descricao);
    form.append('numeros_series', JSON.stringify(ns));
    if (declaracao) {
      form.append('declaracao', declaracao);
    }
    if (boletim) {
      form.append('boletim_ocorrencia', boletim);
    }
    if (outro) {
      form.append('outro', outro);
    }
    form.append('apolice_propria', possui_apolice.toString());
    if (possui_apolice && apolice) form.append('apolice_seguro', apolice);

    setFetching(true);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    let request;
    if (!isEdit) {
      request = apiSinister.post(`comunicar-sinistro/`, form, config);
    } else {
      request = apiSinister.put(
        `comunicar-sinistro/${sinisterId}/`,
        form,
        config,
      );
    }

    request
      .then(() => {
        toast({
          title: `Sinistro ${isEdit ? 'editado' : 'adicionado'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao ${isEdit ? 'editar' : 'adicionar'} sinistro`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao ${isEdit ? 'editar' : 'adicionar'} sinistro`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleSelectedNmrSerieChange = (items?: SeriesNumber[]) => {
    setSelectedNmrSerie(items || []);
    clearErrors('nmr_serie');
  };

  const handleBuscaNmrSerieChange = (texto?: string) => {
    setBuscaNmrSerie(texto || '');
  };

  function handleDescricao(e: any) {
    setDescricao(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('descricao');
    }
  }

  function handleTipo(e: any) {
    clearErrors('boletim');
    precisaBO(e.target.value);
    setTipo(e.target.value);
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('tipo');
    }
  }

  const precisaBO = (id: string) => {
    const tp = typesAux?.filter(it => {
      return it.id === Number(id);
    });
    if (tp && tp[0]) {
      setMostrarBoletimAlert(tp[0].requer_boletim_ocorrencia);
      tp[0].requer_boletim_ocorrencia ? onOpenAlert() : '';
    }
  };

  const handleDeclaracaoChange = (_file: File | null) => {
    setSelectedDeclaracao(_file);
    setNomeDeclaracao(_file ? _file.name : '');
    clearErrors('declaracao');
  };
  const clearDeclaracao = () => {
    setSelectedDeclaracao(null);
    setNomeDeclaracao('');
    setValue('declaracao', null);
  };

  const handleBoletimChange = (_file: File | null) => {
    setSelectedBoletim(_file);
    setNomeBoletim(_file ? _file.name : '');
    clearErrors('boletim');
  };
  const clearBoletim = () => {
    setSelectedBoletim(null);
    setNomeBoletim('');
    setValue('boletim', null);
  };

  const handleOutroChange = (_file: File | null) => {
    setSelectedOutro(_file);
    setNomeOutro(_file ? _file.name : '');
    clearErrors('outro');
  };
  const clearOutro = () => {
    setSelectedOutro(null);
    setNomeOutro('');
    setValue('outro', null);
  };

  const handleApoliceChange = (_file: File | null) => {
    setSelectedApolice(_file);
    setNomeApolice(_file ? _file.name : '');
    clearErrors('apolice');
  };
  const clearApolice = () => {
    setSelectedApolice(null);
    setNomeApolice('');
    setValue('apolice', null);
  };

  return (
    <>
      {!isEdit && (
        <Button
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          onClick={handleOpen}
        >
          Comunicar Sinistro
        </Button>
      )}
      {isEdit && <MenuItem onClick={handleOpen}>Revisar sinistro</MenuItem>}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="md"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>
            {isEdit ? 'Revisar Sinistro' : 'Comunicar Sinistro'}
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={400} w="100%">
              <Stack gridGap={4}>
                {isEdit && (
                  <Alert
                    bg="rgba(255, 215, 71, 0.4);"
                    color="#88460D"
                    display="block"
                    fontSize="16px"
                    borderRadius={10}
                  >
                    <Heading color="#88460D" fontSize="18px" marginBottom={3}>
                      O que deve ser corrigido?
                    </Heading>
                    {revisaoComentario}
                  </Alert>
                )}
                {!isEdit && mostrarBoletimAlert && isVisible && (
                  <Alert
                    bg="rgba(255, 215, 71, 0.4);"
                    color="#88460D"
                    fontSize="16px"
                    borderRadius={10}
                  >
                    <Box>
                      <AlertDescription>
                        Verifique a correspondência do número de série no
                        Boletim de Ocorrência (BO) anexado.
                      </AlertDescription>
                    </Box>
                    <CloseButton
                      alignSelf="flex-start"
                      position="relative"
                      right={-1}
                      top={-1}
                      onClick={onCloseAlert}
                    />
                  </Alert>
                )}
                <FormControl isInvalid={errors.nmr_serie}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    N° de Série *
                    <Tooltip
                      hasArrow
                      label="Informar n° de série para o sinistro."
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={FiHelpCircle} />
                    </Tooltip>
                  </FormLabel>
                  <ReactSelect
                    name="nmr_serie"
                    isMulti
                    isSearchable
                    placeholder="Série n°"
                    options={nmr_serie.map(it => {
                      return {
                        ...it,
                        value: it.numero_serie,
                        label: it.numero_serie,
                      };
                    })}
                    value={
                      isEdit
                        ? selectedNmrSerie.map(it => {
                            return {
                              value: it.numero_serie,
                              label: it.numero_serie,
                            };
                          })
                        : selectedNmrSerie
                    }
                    isLoading={false}
                    onChange={changes =>
                      handleSelectedNmrSerieChange(changes as SeriesNumber[])
                    }
                    onInputChange={change => {
                      handleBuscaNmrSerieChange(change);
                    }}
                    isDisabled={isEdit}
                    defaultStyle
                    noOptionsText="Não há opções"
                    error={errors?.nmr_serie}
                    styles={{
                      menu: provided => ({
                        ...provided,
                        maxHeight: 250,
                      }),
                      menuList: provided => ({
                        ...provided,
                        maxHeight: 250,
                      }),
                      control: provided => ({
                        ...provided,
                        borderColor: errors?.nmr_serie ? '#E53E3E' : '',
                      }),
                    }}
                  />
                </FormControl>
                <FormControl isInvalid={errors.descricao}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Descrição *
                  </FormLabel>
                  <Textarea
                    name="descricao"
                    resize="none"
                    error={errors?.descricao}
                    value={descricao}
                    onChange={e => handleDescricao(e)}
                    fontSize="15px"
                    placeholder="Entre com a descrição"
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.tipo}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Tipo de sinistro *
                  </FormLabel>
                  <InputGroup>
                    <Select
                      name="tipo"
                      placeholder="Selecione"
                      error={errors.tipo}
                      onChange={e => handleTipo(e)}
                      fontSize="15px"
                      value={tipo}
                      isDisabled={isFetching}
                    >
                      {!isTypeLoading &&
                        types?.map(t => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                    </Select>
                    {/* <SinistersTypeForm /> */}
                  </InputGroup>
                </FormControl>
                {/* Declaração */}
                <FormControl isInvalid={errors.declaracao}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Declaração *
                    <Tooltip
                      hasArrow
                      label="Declaração explicando qual foi o ocorrido no sinistro. Tipos PDF, JPG ou PNG, máximo 5MG."
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={FiHelpCircle} />
                    </Tooltip>
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(declaracao || nomeDeclaracao) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearDeclaracao}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleDeclaracaoChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, image/gif, .jpg"
                      ref={declaracaoRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        declaracaoRef.current
                          ? declaracaoRef.current.click()
                          : ''
                      }
                      value={nomeDeclaracao || ''}
                      name="declaracao"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.declaracao}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
                {/* Boletim */}
                <FormControl isInvalid={errors.boletim}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Boletim de Ocorrência (BO) {mostrarBoletimAlert ? '*' : ''}
                    <Tooltip
                      hasArrow
                      label="Informar o boletim de ocorrência (BO). Tipos PDF, JPG ou PNG, máximo 5MG."
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={FiHelpCircle} />
                    </Tooltip>
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(boletim || nomeBoletim) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearBoletim}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleBoletimChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, .jpg"
                      ref={boletimRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        boletimRef.current ? boletimRef.current.click() : ''
                      }
                      value={nomeBoletim || ''}
                      name="boletim"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.boletim}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
                {/* Outro */}
                <FormControl isInvalid={errors.declaracao}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Outro
                    <Tooltip
                      hasArrow
                      label="Outro tipo de arquivo caso necessário. Tipos PDF, JPG ou PNG, máximo 5MG."
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={FiHelpCircle} />
                    </Tooltip>
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiFile} />
                    </InputLeftElement>
                    {(outro || nomeOutro) && (
                      <InputRightElement pointerEvents="auto">
                        <Icon
                          as={FiX}
                          style={{
                            cursor: 'pointer',
                            pointerEvents: isFetching ? 'none' : 'auto',
                          }}
                          onClick={clearOutro}
                        />
                      </InputRightElement>
                    )}
                    <input
                      id="file"
                      type="file"
                      onChange={e => {
                        handleOutroChange(
                          e.target.files ? e.target.files[0] : null,
                        );
                        e.target.value = '';
                      }}
                      accept=".pdf, image/png, image/gif, .jpg"
                      ref={outroRef}
                      style={{ display: 'none' }}
                    />
                    <Input
                      placeholder="Selecione um arquivo"
                      onClick={() =>
                        outroRef.current ? outroRef.current.click() : ''
                      }
                      value={nomeOutro || ''}
                      name="outro"
                      style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      isReadOnly
                      error={errors.outro}
                      isDisabled={isFetching}
                    />
                  </InputGroup>
                </FormControl>
                {/* Apólice */}
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Possui apólice de seguro próprio?
                  <Checkbox
                    isChecked={possui_apolice}
                    marginLeft="10px"
                    onChange={e => {
                      setPossuiApolice(e.target.checked);
                      clearErrors('apolice');
                    }}
                    iconColor="black"
                  />
                </FormLabel>
                {possui_apolice && (
                  <Text>
                    Favor, enviar apólice de seguro pelo campo abaixo:
                  </Text>
                )}
                {possui_apolice && (
                  <FormControl isInvalid={errors.apolice}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      justifyContent="space-between"
                      display="flex"
                    >
                      Apólice de seguro *
                      <Tooltip
                        hasArrow
                        label="Apólice de seguro. Tipos PDF, JPG ou PNG, máximo 5MG."
                        bg="gray"
                        shouldWrapChildren
                      >
                        <Icon as={FiHelpCircle} />
                      </Tooltip>
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FiFile} />
                      </InputLeftElement>
                      {(apolice || nomeApolice) && (
                        <InputRightElement pointerEvents="auto">
                          <Icon
                            as={FiX}
                            style={{
                              cursor: 'pointer',
                              pointerEvents: isFetching ? 'none' : 'auto',
                            }}
                            onClick={clearApolice}
                          />
                        </InputRightElement>
                      )}
                      <input
                        id="file"
                        type="file"
                        onChange={e => {
                          handleApoliceChange(
                            e.target.files ? e.target.files[0] : null,
                          );
                          e.target.value = '';
                        }}
                        accept=".pdf, image/png, image/gif, .jpg"
                        ref={apoliceRef}
                        style={{ display: 'none' }}
                      />
                      <Input
                        placeholder="Selecione um arquivo"
                        onClick={() =>
                          apoliceRef.current ? apoliceRef.current.click() : ''
                        }
                        value={nomeApolice || ''}
                        name="outro"
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        isReadOnly
                        error={errors.apolice}
                        isDisabled={isFetching}
                      />
                    </InputGroup>
                  </FormControl>
                )}
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              {isEdit ? 'Enviar' : 'Cadastrar'}{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
